/**
 * @author Maria Osama
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2018-08-29 11:00:00
 * @description Implementation of the FAQS react component
 * @filename faqs.tsx
 */
import React from "react";
import { Router } from "../../../utilities/router";
import { FaqsQuestionAndAnswer } from "./faqs-question-answer";
import { SVG } from "../../../component-configuration/svgs";

/**
 *
 * A React component that renders the FAQS tab content .
 *
 * @extends {React.Component}
 */
export class FAQS extends React.Component {
  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    return (
      <div className="FAQS external inner-pages">
        <h1 className="font-weight-bold">FAQs</h1>
        <h4 className="font-weight-400">
          We're always looking for ways to make our community better for you.
        </h4>

        <div className="row">
          <div className="col-md-8">
            <div className="question-and-answer">
              <FaqsQuestionAndAnswer
                question="How do I create an account?"
                answer={
                  <p className="answer">
                    To join the other awesome members of our growing community,
                    all you have to do is click “Join” in the menu bar at the
                    top of the page if you’re viewing this page on a desktop,
                    laptop, or tablet. If you’ve arrived here on a mobile
                    device, first click the “☰” icon at the top of the page then
                    select “Join” in the menu that appears.
                  </p>
                }
              />

              <FaqsQuestionAndAnswer
                question="How do I reset my password?"
                answer={
                  <p className="answer">
                    To reset your password please select the reset password link
                    in the dialog displayed after clicking the sign-in option in
                    the menu bar at the top of the page. You will receive an
                    email containing a code which is used to start the process
                    of resetting your passwword. You may need to logout of your
                    current session to display the sign-in option.
                  </p>
                }
              />

              <FaqsQuestionAndAnswer
                question="How do I edit my profile?"
                answer={
                  <p className="answer">
                    Once you’ve signed in to your account, you are able to
                    access all profile settings from the {SVG.personAlt} icon
                    which appears in the menu bar at the top of the screen. Once
                    you add a profile picture to your account, this image will
                    appear in that circle. After selecting {SVG.personAlt} ,
                    click on “Profile Settings” in the menu that appears to edit
                    your profile.
                  </p>
                }
              />

              <FaqsQuestionAndAnswer
                question="Where can I browse for companies and people?"
                answer={
                  <p className="answer">
                    You can view and search all of the companies and people on
                    the platform by selecting “Companies” and “People” in the
                    menu bar at the top of the page. If you’re on mobile, first
                    click the “☰” icon at the top of the page then select
                    “Companies” or “People” in the menu that appears.
                  </p>
                }
              />

              <FaqsQuestionAndAnswer
                question="How can I claim my personal or company page?"
                answer={
                  <p className="answer">
                    See a page about you or your company?
                    <br />
                    To claim your page, please message{" "}
                    <a
                      className="email-link"
                      href="mailto:support@radivision.com"
                    >
                      support@radivision.com
                    </a>{" "}
                    and we will help you claim it.
                  </p>
                }
              />

              <FaqsQuestionAndAnswer
                question="How do I add a company page?"
                answer={
                  <p className="answer">
                    In order to create a company page, you must first create an
                    account for yourself. If you’ve already created an account
                    and signed in, you can submit a company page for approval by
                    creating a company page from your account page.
                    <br />
                    For an existing company page, please message{" "}
                    <a
                      className="email-link"
                      href="mailto:support@radivision.com"
                    >
                      support@radivision.com
                    </a>{" "}
                    and we will help you claim it.
                  </p>
                }
              />

              <FaqsQuestionAndAnswer
                question="How do I nominate a mentor?"
                answer={
                  <p className="answer">
                    Our team is always on the lookout for inspiring
                    entrepreneurial minds to feature on our original,
                    award-winning series. If you’d like to nominate someone you
                    know or yourself to be featured on our platform, click
                    “Nominate” in the menu bar at the top of the page. If you’re
                    on mobile, first click the “☰” icon at the top of the page
                    then select “Nominate” in the menu that appears.
                  </p>
                }
              />

              <FaqsQuestionAndAnswer
                question="Where can I read about privacy and legal terms and conditions?"
                answer={
                  <p className="answer">
                    To read about privacy or legal terms and conditions while
                    engaging with Radivision’s content and community, select
                    <a
                      onClick={() =>
                        Router.to("legal", undefined, "privacy-policy")
                      }
                    >
                      Privacy Policy
                    </a>
                    or
                    <a
                      onClick={() =>
                        Router.to("legal", undefined, "terms-conditions")
                      }
                    >
                      Terms & Conditions
                    </a>
                    . If you have any questions or concerns, don’t hesitate to
                    send us a message.
                  </p>
                }
              />

              <FaqsQuestionAndAnswer
                question="Do you have a question that we didn’t answer?"
                answer={
                  <p className="answer">
                    If you still need help navigating Radivision, please{" "}
                    <a
                      onClick={() =>
                        Router.to("support", undefined, "contact-us")
                      }
                    >
                      send us a message
                    </a>{" "}
                    so that our team can get you back on your way as soon as
                    possible.
                  </p>
                }
              />
            </div>
          </div>

          <div className="col-md-4 right-side">
            <div className="right-side-content">
              <div>
                <h3>Common browser and device issue that you may encounter:</h3>
                <h3>
                  <a
                    onClick={() =>
                      Router.to("support", undefined, "technical-issues")
                    }
                  >
                    Technical Issues
                  </a>
                </h3>
              </div>
              <div>
                <h3>
                  We're always looking for ways to make our community better for
                  you:
                </h3>

                <h3>
                  <a
                    onClick={() =>
                      Router.to("support", undefined, "report-an-issue")
                    }
                  >
                    Report an Issue
                  </a>
                </h3>
              </div>
              <div>
                <h3>Connect with us at : </h3>
                <h3>
                  <a
                    className="email-link"
                    href="mailto:support@radivision.com"
                  >
                    support@radivision.com
                  </a>
                </h3>
                <h3 className="or"> or</h3>
                <h3>
                  <a
                    onClick={() =>
                      Router.to("support", undefined, "contact-us")
                    }
                  >
                    Contact Us
                  </a>
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
